'use client';

import { ActionType } from '@diritto/common/app/inapp-event.constant';
import { HeaderNavMenu, WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { Color } from '@diritto/common/style/color';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { DirittoMessageInput } from 'app-components/common/DirittoMessageInput';
import { FancyShareContentItemMeta } from 'app-components/contents/FancyShareContentItemMeta';
import { FancyShareJsonLd } from 'app-components/fancyShare/FancyShareJsonLd';
import { FancyShareItem } from 'app-components/fancyShare/FancyShareItem';
import { FancyShareAction } from 'constants/fancyShare';
import { DesktopContentWidth, isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { useInAppState } from 'contexts/InAppProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetLikedFancySharesQuery } from 'queries/fancyShare/useGetLikedFancySharesQuery';
import { useGetFancyShareQuery } from 'queries/fancyShare/userGetFancyShareQuery';
import { useGetRelayFancyShareQuery } from 'queries/relay/useGetRelayFancyShareQuery';
import React, { useCallback, useEffect } from 'react';
import { useFancyShareDispatch } from 'state-hooks/fancyShare/useFancyShareDispatch';
import { FullPageLoading } from 'ui-components/common/Loading';
import { ErrorStatusView } from 'ui-components/error/ErrorStatusView';
import { ErrorView } from 'ui-components/error/ErrorView';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { ShareButton } from 'ui-components/navigations/HeaderButtons';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

interface FancyShareDetailPageProps {
  fancyShareId: number;
  isApp?: boolean;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.FancyShareDetail,
};

export const FancyShareDetailPage = ({ fancyShareId, isApp }: FancyShareDetailPageProps) => {
  useInAppHeader({
    screen: WebViewScreen.FancyShareDetail,
    rightMenuList: [HeaderNavMenu.Share],
  });
  useInAppStatusBar(WebViewScreen.FancyShareDetail);

  const { tracker } = useTrackingState();
  useEffect(() => {
    tracker.track(TrackingEventType.ViewFancyShareDetail, { fancyShareId });
  }, [fancyShareId, tracker]);

  const { addActionEventListener } = useInAppState();

  const { data: relayData } = useGetRelayFancyShareQuery(fancyShareId);
  const { data, isLoading: isInitialLoading, refetch } = useGetFancyShareQuery(fancyShareId);
  const fancyShare = data?.fancyShare ?? relayData?.fancyShare;

  const { data: likeData } = useGetLikedFancySharesQuery([fancyShareId]);
  const likeFancyShareIds = likeData?.likeFancyShareIds ?? [];

  const contentPublic = fancyShare && !fancyShare.isDeleted ? relayData?.contents[fancyShare.contentId] : undefined;
  const episodePublic = fancyShare && !fancyShare.isDeleted ? relayData?.episodes[fancyShare.episodeId] : undefined;
  const profilePublic = fancyShare && !fancyShare.isDeleted ? relayData?.profiles[fancyShare.profileId] : undefined;
  const authorProfilePublic =
    contentPublic && contentPublic.profileId ? relayData?.profiles[contentPublic.profileId] : undefined;

  const {
    dispatchAction,
    fancyShare: editFancyShare,
    isPending: isActionPending,
    isInputFocus,
  } = useFancyShareDispatch(refetch);

  const handleShare = useCallback(() => {
    if (fancyShare && !fancyShare.isDeleted) {
      dispatchAction({
        action: FancyShareAction.Share,
        fancyShare,
        content: contentPublic,
        authorProfile: authorProfilePublic,
      });
    }
  }, [authorProfilePublic, contentPublic, dispatchAction, fancyShare]);

  useEffect(
    () =>
      addActionEventListener(params => {
        switch (params.type) {
          case ActionType.Share: {
            handleShare();
            break;
          }
          default:
            break;
        }
      }),
    [addActionEventListener, authorProfilePublic, contentPublic, dispatchAction, fancyShare, handleShare],
  );

  if (!fancyShare && isInitialLoading) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        <FullPageLoading />
      </MutableHeaderLayout>
    );
  }

  if (!fancyShare || fancyShare.isDeleted) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        {!fancyShare && <ErrorStatusView statusCode={404} />}
        {fancyShare && fancyShare.isDeleted && <ErrorView description="삭제된 콘텐츠예요." />}
      </MutableHeaderLayout>
    );
  }

  return (
    <>
      {contentPublic && authorProfilePublic && (
        <FancyShareJsonLd fancyShare={fancyShare} content={contentPublic} authorProfile={authorProfilePublic} />
      )}
      <MutableHeaderLayout isApp={isApp} rightButtons={[<ShareButton key="share" onClick={handleShare} />]}>
        {isInputFocus && (
          <DirittoMessageInput
            initialValue={editFancyShare?.description}
            isLoading={isActionPending}
            onCancelWrite={() => dispatchAction({ action: FancyShareAction.CancelEdit })}
            onRegister={(description: string) =>
              editFancyShare &&
              dispatchAction({ action: FancyShareAction.Update, fancyShare: editFancyShare, data: { description } })
            }
            maxLength={1000}
            placeholder="이 문장을 공유하고 싶은 이유를 글로 적어보세요"
            className={`fixed bottom-0 z-[${ZIndex.FancyShareInput}] ${isPC ? `w-[${DesktopContentWidth - 2}px]` : 'w-full'}`}
          />
        )}
        <ContentBodyPaddingContainer>
          {contentPublic && authorProfilePublic && (
            <div className="mt-4">
              <FancyShareContentItemMeta content={contentPublic} profile={authorProfilePublic} color={Color.Black} />
            </div>
          )}
          <div className="flex flex-col items-center mt-4 mb-24">
            <FancyShareItem
              key={fancyShare.id}
              fancyShare={fancyShare}
              isLiked={likeFancyShareIds.includes(fancyShare.id)}
              profile={profilePublic}
              episode={episodePublic}
              content={contentPublic}
              onDispatchAction={dispatchAction}
              trackerExtraInfo={trackerExtraInfo}
            />
          </div>
        </ContentBodyPaddingContainer>
      </MutableHeaderLayout>
    </>
  );
};
